import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import useAuthStore from "@/store/authStore";
import { Shield, Save, RotateCcw, Search, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTIONS = ["READ", "CREATE", "UPDATE", "DELETE"];

// Friendly labels for entity rows in the matrix.
const ENTITY_LABELS = {
  Alert: "Alerts",
  AuditLog: "Audit Log",
  BOM: "Bill of Materials",
  Branch: "Branches",
  Brand: "Brands",
  Buyer: "Buyers / Customers",
  BuyerSKU: "Buyer SKUs",
  Dispatch: "Dispatches",
  DispatchLot: "Dispatch Lots",
  Event: "Event Bus",
  FGInventory: "FG Inventory",
  Forecast: "Demand Forecasts",
  IBTTransfer: "IBT Transfers",
  Invoice: "Invoices",
  InventoryImport: "Inventory Imports",
  Model: "Models",
  PriceHistory: "RM Price History",
  ProductionBatch: "Production Batches",
  ProductionPlan: "Production Plans / Schedule",
  PurchaseOrder: "Purchase Orders",
  PurchaseOrderLine: "PO Lines",
  QCApproval: "QC Approvals",
  QCChecklist: "QC Checklists",
  QCResult: "QC Results",
  RMStockMovement: "RM Stock Movements",
  RMInwardBill: "RM Inward Bills",
  RawMaterial: "Raw Materials",
  Role: "Roles",
  SystemConfig: "System Config",
  User: "Users",
  Vendor: "Vendors",
  VendorRMPrice: "Vendor RM Prices",
  Vertical: "Verticals",
};

const RoleManagement = () => {
  const { token, user } = useAuthStore();
  const isMasterAdmin = user?.role === "master_admin";

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [grantedIds, setGrantedIds] = useState(new Set());
  const [originalIds, setOriginalIds] = useState(new Set());
  const [search, setSearch] = useState("");

  const [loadingRole, setLoadingRole] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---------------------------------------------------------------------
  // Bootstrap: roles + permissions catalog.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [rolesRes, permsRes] = await Promise.all([
          axios.get(`${API}/roles`, { headers }),
          axios.get(`${API}/permissions`, { headers }),
        ]);
        const roleList = (rolesRes.data || []).filter((r) => r.is_active);
        setRoles(roleList);
        setPermissions(permsRes.data || []);
        // Auto-pick first non-MASTER_ADMIN role
        const firstEditable = roleList.find((r) => r.code !== "MASTER_ADMIN");
        if (firstEditable) setSelectedRole(firstEditable.code);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load roles/permissions");
      }
    })();
  }, [token]);

  // ---------------------------------------------------------------------
  // Whenever role changes, fetch its currently granted permission_ids.
  // ---------------------------------------------------------------------
  const loadRolePermissions = useCallback(async (roleCode) => {
    if (!roleCode || !token) return;
    setLoadingRole(true);
    try {
      const res = await axios.get(`${API}/roles/${roleCode}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ids = new Set(res.data.permission_ids || []);
      setGrantedIds(ids);
      setOriginalIds(new Set(ids));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load role permissions");
    } finally {
      setLoadingRole(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedRole) loadRolePermissions(selectedRole);
  }, [selectedRole, loadRolePermissions]);

  // ---------------------------------------------------------------------
  // Build matrix: { entity: { READ: perm, CREATE: perm, ... } }
  // ---------------------------------------------------------------------
  const matrix = useMemo(() => {
    const m = {};
    for (const p of permissions) {
      if (!m[p.entity]) m[p.entity] = {};
      m[p.entity][p.action] = p;
    }
    return m;
  }, [permissions]);

  const entities = useMemo(() => {
    const list = Object.keys(matrix).sort((a, b) => {
      const la = ENTITY_LABELS[a] || a;
      const lb = ENTITY_LABELS[b] || b;
      return la.localeCompare(lb);
    });
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(
      (e) =>
        e.toLowerCase().includes(q) ||
        (ENTITY_LABELS[e] || "").toLowerCase().includes(q)
    );
  }, [matrix, search]);

  const isMasterAdminRole = selectedRole === "MASTER_ADMIN";
  const isDirty = useMemo(() => {
    if (grantedIds.size !== originalIds.size) return true;
    for (const id of grantedIds) if (!originalIds.has(id)) return true;
    return false;
  }, [grantedIds, originalIds]);

  // ---------------------------------------------------------------------
  // Toggle helpers
  // ---------------------------------------------------------------------
  const togglePerm = (permId) => {
    if (!permId || isMasterAdminRole) return;
    setGrantedIds((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  };

  const toggleEntityRow = (entity, allOn) => {
    if (isMasterAdminRole) return;
    setGrantedIds((prev) => {
      const next = new Set(prev);
      for (const action of ACTIONS) {
        const p = matrix[entity]?.[action];
        if (!p) continue;
        if (allOn) next.delete(p.id);
        else next.add(p.id);
      }
      return next;
    });
  };

  const toggleActionColumn = (action, allOn) => {
    if (isMasterAdminRole) return;
    setGrantedIds((prev) => {
      const next = new Set(prev);
      for (const entity of entities) {
        const p = matrix[entity]?.[action];
        if (!p) continue;
        if (allOn) next.delete(p.id);
        else next.add(p.id);
      }
      return next;
    });
  };

  const handleReset = () => {
    setGrantedIds(new Set(originalIds));
  };

  const handleSave = async () => {
    if (!selectedRole || isMasterAdminRole) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/roles/${selectedRole}/permissions`,
        { permission_ids: Array.from(grantedIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { added, removed } = res.data || {};
      toast.success(
        `Saved — +${added || 0} added, -${removed || 0} removed`
      );
      setOriginalIds(new Set(grantedIds));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------
  // Row/column "all on" helpers for header checkbox tri-state
  // ---------------------------------------------------------------------
  const entityRowState = (entity) => {
    const perms = ACTIONS.map((a) => matrix[entity]?.[a]).filter(Boolean);
    if (!perms.length) return { all: false, some: false };
    const granted = perms.filter((p) => grantedIds.has(p.id)).length;
    return { all: granted === perms.length, some: granted > 0 && granted < perms.length };
  };

  const colState = (action) => {
    const perms = entities.map((e) => matrix[e]?.[action]).filter(Boolean);
    if (!perms.length) return { all: false, some: false };
    const granted = perms.filter((p) => grantedIds.has(p.id)).length;
    return { all: granted === perms.length, some: granted > 0 && granted < perms.length };
  };

  if (!isMasterAdmin) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-800">
          <Shield className="w-6 h-6 mb-2" />
          Role Management is restricted to Master Admins.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="role-management-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" /> Role Permissions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Toggle Read / Create / Update / Delete per sub-module for each role. Master Admin has implicit full access and cannot be edited.
          </p>
          <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 mt-2 inline-block" data-testid="rbac-enforcement-banner">
            Endpoint-level enforcement is live for 18 entities (Alerts, BOM, BuyerSKU, Dispatch, DispatchLot, FGInventory, FGInwardBill, Forecast, IBTTransfer, Invoice, ProductionPlan, RawMaterial, RMInwardBill, RMStockMovement, Role, SystemConfig, User, Vendor, VendorRMPrice). AuditLog · Branch · Brand · Vertical · Model · PurchaseOrder remain UI-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!isDirty || saving || isMasterAdminRole}
            data-testid="rbac-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isDirty || saving || isMasterAdminRole}
            data-testid="rbac-save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-1" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-gray-700">Role:</Label>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-72" data-testid="rbac-role-select">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {roles.map((r) => (
                <SelectItem key={r.code} value={r.code} data-testid={`rbac-role-option-${r.code}`}>
                  {r.name}{" "}
                  {r.code === "MASTER_ADMIN" && (
                    <span className="text-xs text-gray-500">(read-only)</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedRole && (
            <Badge variant="secondary" className="ml-1">
              {grantedIds.size} permissions granted
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Search sub-module..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-64"
              data-testid="rbac-search-input"
            />
          </div>
        </div>
      </div>

      {/* Matrix */}
      <div className="border rounded-lg overflow-hidden bg-white" data-testid="rbac-matrix">
        {isMasterAdminRole && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-sm text-blue-800 flex items-center gap-2">
            <Lock className="w-4 h-4" /> MASTER_ADMIN has implicit full access — this matrix is read-only.
          </div>
        )}
        {loadingRole ? (
          <div className="p-10 text-center text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading permissions…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 w-72">
                    Sub-module
                  </th>
                  {ACTIONS.map((action) => {
                    const st = colState(action);
                    return (
                      <th
                        key={action}
                        className="px-4 py-3 font-medium text-gray-700 text-center"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Checkbox
                            checked={st.all}
                            data-state={st.some ? "indeterminate" : st.all ? "checked" : "unchecked"}
                            onCheckedChange={() => toggleActionColumn(action, st.all)}
                            disabled={isMasterAdminRole}
                            data-testid={`rbac-col-toggle-${action}`}
                          />
                          <span>{action}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {entities.length === 0 && (
                  <tr>
                    <td colSpan={ACTIONS.length + 1} className="text-center py-10 text-gray-500">
                      No sub-modules match your search.
                    </td>
                  </tr>
                )}
                {entities.map((entity) => {
                  const rs = entityRowState(entity);
                  return (
                    <tr key={entity} className="border-t hover:bg-gray-50" data-testid={`rbac-row-${entity}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={rs.all}
                            data-state={rs.some ? "indeterminate" : rs.all ? "checked" : "unchecked"}
                            onCheckedChange={() => toggleEntityRow(entity, rs.all)}
                            disabled={isMasterAdminRole}
                            data-testid={`rbac-row-toggle-${entity}`}
                          />
                          <div>
                            <div className="font-medium text-gray-800">
                              {ENTITY_LABELS[entity] || entity}
                            </div>
                            <div className="text-xs text-gray-500">{entity}</div>
                          </div>
                        </div>
                      </td>
                      {ACTIONS.map((action) => {
                        const p = matrix[entity]?.[action];
                        if (!p) {
                          return (
                            <td key={action} className="text-center text-gray-300">
                              —
                            </td>
                          );
                        }
                        const checked = grantedIds.has(p.id);
                        return (
                          <td key={action} className="text-center px-4 py-2.5">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => togglePerm(p.id)}
                              disabled={isMasterAdminRole}
                              data-testid={`rbac-cell-${entity}-${action}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Tiny inline label component (avoids reaching for shadcn Label if unused).
const Label = ({ children, className = "" }) => (
  <span className={className}>{children}</span>
);

export default RoleManagement;
